import React, { FC, PropsWithChildren, useCallback, useState } from "react";

import { RealTimeWrapper } from "components";
import toast from "react-hot-toast";
import { useCurrentUser } from "_graphql/cache/auth";

type Props = {};

type BatchReportContextType = {
  showBanner: () => void;
  hideBanner: () => void;
  bannerVisible: boolean;
};

const BatchReportContext = React.createContext<
  BatchReportContextType | undefined
>(undefined);

export const useBatchReportContext = () => {
  const context = React.useContext(BatchReportContext);
  if (context === undefined) {
    throw new Error("useBatchReport must be used within a BatchReportProvider");
  }
  return context;
};

const BatchReportProvider: FC<PropsWithChildren<Props>> = ({ children }) => {
  const user = useCurrentUser();
  const [bannerVisible, setBannerVisible] = useState(false);

  const employee = user?.employee || null;

  const showBanner = useCallback(() => setBannerVisible(true), []);
  const hideBanner = useCallback(() => setBannerVisible(false), []);

  return (
    <RealTimeWrapper
      channel="LgzULSz64aed7WJ7YwDq3qmHAeX786dZNZ3Ko4Bisf0"
      events={[
        `${employee?.employee_id}.batch-report.success`,
        `${employee?.employee_id}.batch-report.failed`,
        `${employee?.employee_id}.batch-report.pending`,
      ]}
      callBack={(data) => {
        console.log("event", data);
        const event = data?.status.toLowerCase();
        if (!event) return;
        if (event === "success") {
          hideBanner();
          toast.success("Batch report generated successfully!", {
            duration: 5000,
            position: "top-center",
            style: {
              background: "#f0f4ff",
              color: "#1e40af",
              border: "1px solid #cbd5e1",
              borderRadius: "0.375rem",
              padding: "0.5rem 1rem",
              fontSize: "0.875rem",
            },
          });
        } else if (event === "failed") {
          hideBanner();
          toast.error("Failed to generate batch report. Please try again.", {
            duration: 5000,
            position: "top-center",
            style: {
              background: "#fee2e2",
              color: "#b91c1c",
              border: "1px solid #fca5a5",
              borderRadius: "0.375rem",
              padding: "0.5rem 1rem",
              fontSize: "0.875rem",
            },
          });
        } else if (event === "pending") {
          showBanner();
        }
      }}
    >
      <BatchReportContext.Provider
        value={{ showBanner, hideBanner, bannerVisible }}
      >
        {/* Closable notification bar */}
        {bannerVisible && (
          <div className="fixed top-6 left-1/2 transform -translate-x-1/2 w-full max-w-lg z-50">
            <div className="flex items-start bg-white border border-gray-300 shadow-xl rounded-md px-5 py-4 space-x-4 animate-fade-in relative">
              <div className="flex-shrink-0 mt-1">
                {/* Microsoft-style spinner */}
                <svg
                  className="w-7 h-7 text-blue-600 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-20"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-80"
                    fill="currentColor"
                    d="M12 2a10 10 0 0 1 10 10h-4a6 6 0 0 0-6-6V2z"
                  />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-semibold text-gray-900">
                    Report Generation in Progress
                  </span>
                  <span className="ml-2 px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-medium">
                    Processing
                  </span>
                </div>
                <p className="mt-1 text-sm text-gray-700">
                  Your requested report is being generated. This may take a few
                  moments.
                </p>
                <div className="mt-2 flex items-center space-x-2">
                  <svg
                    className="w-4 h-4 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 17l4 4 4-4m-4-5v9"
                    />
                  </svg>
                  <span className="text-xs text-gray-500">
                    You can continue working while your report is being
                    prepared.
                  </span>
                </div>
              </div>
              {/* Close button */}
              <button
                onClick={hideBanner}
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 focus:outline-none"
                aria-label="Close notification"
                type="button"
              >
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 8.586l4.95-4.95a1 1 0 111.414 1.414L11.414 10l4.95 4.95a1 1 0 01-1.414 1.414L10 11.414l-4.95 4.95a1 1 0 01-1.414-1.414L8.586 10l-4.95-4.95A1 1 0 115.05 3.636L10 8.586z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
        {children}
      </BatchReportContext.Provider>
    </RealTimeWrapper>
  );
};

export default BatchReportProvider;
